import React from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import ElderlyIcon from "@mui/icons-material/Elderly";
import SchoolIcon from "@mui/icons-material/School";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonIcon from "@mui/icons-material/Person";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { PolicyEmploymentStatus } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { formatPolicyEmploymentStatus } from "../../../utils/enumFormatter";
import { usePartialUpdateContact } from "../../../hooks/crm/useContacts";

export const employmentStatusIcon = (status: number) => {
  switch (status) {
    case PolicyEmploymentStatus.EMPLOYEE:
      return <WorkIcon fontSize="small" />;
    case PolicyEmploymentStatus.SELF_EMPLOYED:
    case PolicyEmploymentStatus.SELF_EMPLOYED_AND_EMPLOYEE:
      return <StorefrontIcon fontSize="small" />;
    case PolicyEmploymentStatus.CONTROLLING_OWNER:
      return <BusinessIcon fontSize="small" />;
    case PolicyEmploymentStatus.REIRED:
      return <ElderlyIcon fontSize="small" />;
    case PolicyEmploymentStatus.UNEMPLOYED:
      return <PersonOffIcon fontSize="small" />;
    case PolicyEmploymentStatus.KIBBUTZ_MEMBER:
      return <AgricultureIcon fontSize="small" />;
    case PolicyEmploymentStatus.STUDENT:
      return <SchoolIcon fontSize="small" />;
    case PolicyEmploymentStatus.SOLDIER:
      return <MilitaryTechIcon fontSize="small" />;
    case PolicyEmploymentStatus.YESHIVA_STUDENT:
      return <MenuBookIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

/** סטטוס תעסוקתי – dropdown מעוצב (RTL, אייקון לכל סטטוס) שנשמר מיד בבחירה */
const EmploymentStatusSelect: React.FC<{ contact: Contact }> = ({ contact }) => {
  const updateContact = usePartialUpdateContact();
  return (
    <FormControl fullWidth disabled={updateContact.isPending}>
      <InputLabel>סטטוס תעסוקתי</InputLabel>
      <Select
        value={contact.employment_status?.toString() || ""}
        onChange={(e) => {
          const value = e.target.value;
          updateContact.mutate({
            id: contact.id!,
            fields: { employment_status: value ? (Number(value) as PolicyEmploymentStatus) : null } as Partial<Contact>,
          });
        }}
        label="סטטוס תעסוקתי"
        renderValue={(v) =>
          v ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ color: "primary.main", display: "flex" }}>{employmentStatusIcon(Number(v))}</Box>
              <Typography fontWeight={600} fontSize="1.05rem">
                {formatPolicyEmploymentStatus(Number(v) as PolicyEmploymentStatus)}
              </Typography>
            </Box>
          ) : (
            ""
          )
        }
        sx={{ borderRadius: 2, backgroundColor: "action.hover", "& .MuiSelect-select": { py: 1.5 } }}
        MenuProps={{
          dir: "rtl",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          PaperProps: { sx: { borderRadius: 2, mt: 0.5, maxHeight: 420 } },
        }}
      >
        <MenuItem value="">
          <Typography color="text.secondary" fontStyle="italic">לא נבחר</Typography>
        </MenuItem>
        {Object.values(PolicyEmploymentStatus)
          .filter((value) => typeof value === "number")
          .map((value) => (
            <MenuItem key={value} value={value.toString()} sx={{ gap: 1.5, py: 1 }}>
              <Box sx={{ color: "text.secondary", display: "flex" }}>{employmentStatusIcon(value as number)}</Box>
              {formatPolicyEmploymentStatus(value as PolicyEmploymentStatus)}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

export default EmploymentStatusSelect;
